import {
  composeTitle,
  getSubcategoryName,
  parseTitleToComponents,
} from "@/lib/listing/titleComposer";
import type { ListingFamilySpecs } from "@/lib/listing/listingFamilyRuntime";
import { extractTitleCohortSignal } from "@/lib/pricingTitleCohort";

export type SellBandTitleInput = {
  title: string;
  category: string;
  subcategory?: string;
  size?: string;
  brand?: string;
  model?: string;
  familySpecs?: ListingFamilySpecs;
};

function tokenCount(value: string): number {
  return value.trim().split(/\s+/).filter(Boolean).length;
}

/**
 * Single source of truth for the title string sent to market band / cohort resolution
 * (sell, bulk, edit client, and server assertPriceInBand).
 */
export function resolveBandTitleForListing(input: SellBandTitleInput): string {
  const trimmedTitle = input.title.trim();

  if (
    trimmedTitle.length >= 5 &&
    extractTitleCohortSignal(trimmedTitle, {
      subcategoryId: input.subcategory,
    }) != null
  ) {
    return trimmedTitle;
  }

  const subcategory = input.subcategory?.trim() || "";
  const composed = composeTitle({
    size: input.size?.trim() || undefined,
    brand: input.brand?.trim() || undefined,
    model: input.model?.trim() || undefined,
    subcategoryId: subcategory || undefined,
    subcategoryName: subcategory
      ? (getSubcategoryName(subcategory) ?? undefined)
      : undefined,
    familySpecs: input.familySpecs,
  }).trim();

  if (!composed) {
    return trimmedTitle;
  }

  if (!trimmedTitle) {
    return composed;
  }

  if (tokenCount(composed) > tokenCount(trimmedTitle)) {
    return composed;
  }

  return trimmedTitle;
}

/** @deprecated Prefer `resolveBandTitleForListing` — kept for existing imports. */
export function resolveSellPriceBandTitle(input: SellBandTitleInput): string {
  return resolveBandTitleForListing(input);
}

/** Band title from a persisted listing row (+ optional PATCH overrides). */
export function resolveBandTitleFromListingRecord(input: {
  title: string;
  category: string;
  subcategory?: string | null;
  brand?: string | null;
  model?: string | null;
  sizeText?: string | null;
}): string {
  const sizeFromOverride = input.sizeText?.trim() || "";
  const sizeFromTitle = parseTitleToComponents(input.title).size?.trim() || "";

  return resolveBandTitleForListing({
    title: input.title,
    category: input.category,
    subcategory: input.subcategory ?? undefined,
    brand: input.brand ?? undefined,
    model: input.model ?? undefined,
    size: sizeFromOverride || sizeFromTitle || undefined,
  });
}
